import type { ViewActionDefinition, ViewData, ViewQueryResult } from '@deskworks/shared-types';
import type { ViewCall, ViewCallResult, ViewInput, ViewMeta, ViewNode, ViewStep } from '@deskworks/view-runtime';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { api, errorMessage } from '@/lib/api';
import { t } from '@/lib/i18n';
import { useRealtimeEnvelope } from '@/lib/realtime';
import { useTheme } from '@/lib/theme';
import { useToast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { KitHostContext, KitRender, type KitHost } from './kit-render';
import { viewEngine } from './view-engine';

export const viewKeys = {
  list: (workspaceId: string, deskId?: string) => ['ws', workspaceId, 'views', deskId ?? null] as const,
  all: (workspaceId: string) => ['ws', workspaceId, 'views'] as const,
  detail: (id: string) => ['view', id] as const,
  data: (id: string, version: number | null, params: Record<string, unknown>) => ['view', id, 'data', version, params] as const,
};

/** Realtime events after which useQuery and useWork load again: the data or the work of the workspace changed. */
const REFRESH_EVENTS = new Set(['workspace_db.changed', 'work_item.updated', 'approval.requested', 'approval.resolved']);

const ACTION_LABEL: Record<ViewActionDefinition['kind'], string> = { approve: t('Approve'), reject: t('Send back'), work: t('Start'), update: t('Save') };

function toInput(
  results: Record<string, ViewQueryResult>,
  params: Record<string, unknown>,
  state: Record<string, unknown>,
  theme: 'light' | 'dark',
  refresh: number,
): ViewInput {
  return {
    data: Object.fromEntries(Object.entries(results).map(([k, v]) => [k, v.rows])),
    meta: Object.fromEntries(Object.entries(results).map(([k, v]) => [k, { columns: v.columns, truncated: v.truncated }])),
    params,
    state,
    theme,
    now: new Date().toISOString(),
    refresh,
  };
}

/**
 * Where a saved view's data comes from for a guest of a shared desk (docs/features/desk-sharing.md): their own calls,
 * no work on rows, no buttons, no pages to go to; the data loads again every minute instead of live.
 */
export interface ViewSource {
  data: (viewId: string, params: Record<string, unknown>) => Promise<ViewData>;
  query: (viewId: string, name: string, params: Record<string, unknown>) => Promise<ViewQueryResult>;
}

/** What Deskworks asks the person before an action runs: a confirmation, a comment, or both. */
interface ActionQuestion {
  title: string;
  ask: string | null;
  label: string;
  destructive: boolean;
  resolve: (answer: { comment?: string } | null) => void;
}

/**
 * Draws one view: its code runs in the sealed engine (Web Worker), its data
 * comes from Deskworks. Saved views (`viewId`) load the live version's data and
 * refresh live when one of their tables changes; previews pass `data` in.
 * The view's calls (useQuery, useWork, actions, navigate, toast) are done here,
 * for the person looking at it; actions only run on the saved view.
 * A render error keeps the last good picture and shows the reason.
 */
export function ViewHost({
  workspaceId,
  viewId,
  code,
  version = null,
  tables = [],
  data: given,
  source,
  className,
}: {
  workspaceId: string;
  viewId?: string;
  code: string;
  version?: number | null;
  tables?: string[];
  /** Preview: data from the preview endpoint instead of the saved view. */
  data?: ViewData | null;
  /** A guest's: see ViewSource. */
  source?: ViewSource;
  className?: string;
}) {
  const key = useId();
  const queryClient = useQueryClient();
  const router = useRouter();
  const toast = useToast();
  const { resolved: theme } = useTheme();
  const [params, setParams] = useState<Record<string, unknown>>({});
  const [state, setState] = useState<Record<string, unknown>>({});
  const [refresh, setRefresh] = useState(0);
  const [tree, setTree] = useState<ViewNode | null>(null);
  const [loaded, setLoaded] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [question, setQuestion] = useState<ActionQuestion | null>(null);
  const meta = useRef<ViewMeta | null>(null);
  const preview = given !== undefined || !viewId;

  const live = useQuery({
    queryKey: source ? ['guest', ...viewKeys.data(viewId ?? '', version, params)] : viewKeys.data(viewId ?? '', version, params),
    queryFn: () => (source ? source.data(viewId!, params) : api.views.data(viewId!, params)),
    enabled: !!viewId && given === undefined,
    placeholderData: (prev) => prev,
    refetchInterval: source ? 60_000 : false,
  });
  const data = given !== undefined ? given : live.data;

  // New data or work while the view is open: refetch the declared queries when a table they read changed (or the
  // structure did), and let useQuery / useWork load again (at most twice a second).
  const bump = useRef<number | null>(null);
  useRealtimeEnvelope((env) => {
    if (env.workspaceId !== workspaceId || !REFRESH_EVENTS.has(env.event)) return;
    if (env.event === 'workspace_db.changed' && viewId) {
      const change = (env.data ?? {}) as { tables?: string[]; schema?: boolean };
      if (change.schema || !tables.length || change.tables?.some((name) => tables.includes(name))) {
        void queryClient.invalidateQueries({ queryKey: ['view', viewId, 'data'] });
      }
    }
    if (bump.current === null) {
      bump.current = window.setTimeout(() => {
        bump.current = null;
        setRefresh((n) => n + 1);
      }, 500);
    }
  });
  useEffect(() => () => void (bump.current !== null && window.clearTimeout(bump.current)), []);

  const ask = useCallback(
    (q: Omit<ActionQuestion, 'resolve'>) => new Promise<{ comment?: string } | null>((resolve) => setQuestion({ ...q, resolve })),
    [],
  );

  const runAction = useCallback(
    async (name: string, row: string | null, input: Record<string, unknown> = {}) => {
      const action = meta.current?.actions?.[name];
      if (!action) throw new Error(`The view has no action "${name}" (declare it in export const actions).`);
      if (preview) throw new Error(t('Actions run on the saved view: save this version first.'));
      if (source) throw new Error(t('The buttons of this view are for the team.'));
      if (row === null || row === '') throw new Error(`The action "${name}" needs the row (its primary key).`);
      let comment = typeof input.comment === 'string' ? input.comment : undefined;
      const askComment = action.ask && comment === undefined ? action.ask : null;
      if (action.confirm || askComment) {
        const answer = await ask({
          title: action.confirm ?? askComment!,
          ask: askComment,
          label: action.label ?? ACTION_LABEL[action.kind],
          destructive: action.kind === 'reject',
        });
        if (!answer) throw new Error(t('Cancelled.'));
        comment = answer.comment ?? comment;
      }
      try {
        const result = await api.views.runAction(viewId!, name, { row, comment });
        toast.success(result.message);
        setRefresh((n) => n + 1);
        return result;
      } catch (err) {
        toast.error(t('The action did not run'), errorMessage(err));
        throw err;
      }
    },
    [ask, preview, source, toast, viewId],
  );

  const navigate = useCallback(
    (to: string) => {
      // Only pages of Deskworks itself; a guest has none besides this one.
      if (source) throw new Error(t('This link opens only for the team.'));
      if (!/^\/w\/[^/]+\//.test(to)) throw new Error('navigate goes to Deskworks pages only (/w/…).');
      router.history.push(to);
    },
    [router, source],
  );

  const handle = useCallback(
    async (call: ViewCall): Promise<unknown> => {
      const args = call.args ?? {};
      switch (call.op) {
        case 'query': {
          const name = String(args.name ?? '');
          const p = (args.params && typeof args.params === 'object' ? args.params : {}) as Record<string, unknown>;
          if (source) return source.query(viewId!, name, p);
          return preview ? api.views.previewQuery(workspaceId, code, name, p) : api.views.query(viewId!, name, p);
        }
        case 'work': {
          const keys = Array.isArray(args.keys) ? args.keys.map(String).slice(0, 500) : [];
          return keys.length && !source ? api.views.rowWork(workspaceId, String(args.table ?? ''), keys) : {};
        }
        case 'action':
          return runAction(String(args.name ?? ''), args.row === null || args.row === undefined ? null : String(args.row), (args.input ?? {}) as Record<string, unknown>);
        case 'navigate':
          return navigate(String(args.to ?? ''));
        case 'toast': {
          const message = String(args.message ?? '').slice(0, 300);
          if (args.tone === 'danger') toast.error(message);
          else if (args.tone === 'success') toast.success(message);
          else toast.info(message);
          return null;
        }
        default:
          throw new Error(`Unknown call "${String(call.op)}".`);
      }
    },
    [code, navigate, preview, runAction, source, toast, viewId, workspaceId],
  );

  // A step from the engine: the new picture, host state, and calls to do (their results go back as new steps).
  const apply = useRef<(step: ViewStep) => void>(() => undefined);
  apply.current = (step: ViewStep) => {
    if (step.tree) {
      setTree(step.tree);
      setError(null);
    }
    if (step.effects.state) setState((s) => ({ ...s, ...step.effects.state }));
    if (step.effects.params) setParams((p) => ({ ...p, ...step.effects.params }));
    for (const call of step.calls) {
      void handle(call)
        .then(
          (value): ViewCallResult => ({ ok: true, value: value ?? null }),
          (err): ViewCallResult => ({ ok: false, error: errorMessage(err) }),
        )
        .then((result) => viewEngine.settle(key, call.id, result))
        .then((next) => apply.current(next))
        .catch((err) => setError(errorMessage(err)));
    }
  };

  // Load (or reload) the code into the engine.
  useEffect(() => {
    let cancelled = false;
    setLoaded(null);
    viewEngine
      .load(key, code)
      .then((m) => {
        if (cancelled) return;
        meta.current = m;
        setLoaded(code);
        setError(null);
      })
      .catch((err) => !cancelled && setError(errorMessage(err)));
    return () => {
      cancelled = true;
    };
  }, [key, code]);
  useEffect(() => () => viewEngine.dispose(key), [key]);

  // Render whenever data, state, theme or the refresh counter change.
  const input = useMemo(() => (data ? toInput(data.results, data.params, state, theme, refresh) : null), [data, state, theme, refresh]);
  useEffect(() => {
    if (!input || loaded !== code) return;
    let cancelled = false;
    viewEngine
      .render(key, input)
      .then((step) => !cancelled && apply.current(step))
      .catch((err) => !cancelled && setError(errorMessage(err)));
    return () => {
      cancelled = true;
    };
  }, [key, input, loaded, code]);

  const onEvent = useCallback(
    (handler: string, arg?: unknown) => {
      viewEngine
        .event(key, handler, arg)
        .then((step) => apply.current(step))
        .catch((err) => setError(errorMessage(err)));
    },
    [key],
  );

  const host = useMemo<KitHost>(
    () => ({
      runAction,
      actionLabel: (name) => {
        const a = meta.current?.actions?.[name];
        return a ? (a.label ?? ACTION_LABEL[a.kind]) : name;
      },
      navigate: (to) => {
        try {
          navigate(to);
        } catch (err) {
          toast.error(errorMessage(err));
        }
      },
    }),
    [navigate, runAction, toast],
  );

  const firstLoad = !tree && !error;
  const loadError = live.isError ? errorMessage(live.error) : null;

  return (
    <div className={cn('relative', className)}>
      {(error || loadError) && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span className="min-w-0 break-words whitespace-pre-wrap">
            {tree ? `${t('Showing the last working picture.')} ` : ''}
            {error ?? loadError}
          </span>
        </div>
      )}
      {firstLoad ? (
        <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> {t('Drawing the view …')}
        </div>
      ) : (
        tree && (
          <KitHostContext.Provider value={host}>
            <KitRender node={tree} onEvent={onEvent} />
          </KitHostContext.Provider>
        )
      )}
      {question && <ActionQuestionDialog question={question} onDone={() => setQuestion(null)} />}
    </div>
  );
}

/** Drawn by Deskworks, never by the view: the view can't answer it for the person. */
function ActionQuestionDialog({ question, onDone }: { question: ActionQuestion; onDone: () => void }) {
  const [comment, setComment] = useState('');
  const finish = (answer: { comment?: string } | null) => {
    question.resolve(answer);
    onDone();
  };
  return (
    <Dialog open onOpenChange={(open) => !open && finish(null)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{question.title}</DialogTitle>
          {question.ask && question.ask !== question.title && <DialogDescription>{question.ask}</DialogDescription>}
        </DialogHeader>
        {question.ask && <Textarea autoFocus rows={4} value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t('Your comment')} />}
        <DialogFooter>
          <Button variant="outline" onClick={() => finish(null)}>
            {t('Cancel')}
          </Button>
          <Button variant={question.destructive ? 'destructive' : 'default'} onClick={() => finish(question.ask ? { comment: comment.trim() } : {})}>
            {question.label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
